const processors = () => {
  const cache = {}
  const indexes = {}

  const storeValue = (target, value, key) => {
    if (cache[key]) return
    target.push(value)
    cache[key] = true
  }

  const detection = (target, data) => {
    const { 'malware-family': malwareFamily } = data
    storeValue(target, malwareFamily, `detection/${malwareFamily}`)
  }

  const network = (target, data) => {
    const { ipv4, ipv6, path = null, port = null } = data

    if (data?.host?.startsWith('http')) {
      if (!('http' in indexes)) {
        const idx = target.push({
          name: 'http',
          type: 'indicator-type',
          items: [],
        })
        indexes.http = idx - 1
      }

      const value = data.host

      return storeValue(
        target[indexes.http].items,
        {
          value,
          isIP: false,
          type: 'URL',
          queryable: true,
        },
        `network/http/${value}`
      )
    }

    if ((data?.host || ipv4 || ipv6) && typeof path === 'string') {
      if (!('http' in indexes)) {
        const idx = target.push({
          name: 'http',
          type: 'indicator-type',
          items: [],
        })
        indexes.http = idx - 1
      }

      const host = data.host || ipv4 || ipv6
      const protocol = port === 443 || port === 8443 ? 'https' : 'http'
      const value = `${protocol}://${host}${path.startsWith('/') ? path : `/${path}`}`

      const containsIP = typeof (ipv4 || ipv6) !== 'undefined'

      return storeValue(
        target[indexes.http].items,
        {
          value,
          isIP: !data.host && containsIP,
          type: 'URL',
          queryable: true,
        },
        `network/http/${value}`
      )
    }

    const host = data.host || ipv4 || ipv6

    const containsIP = typeof (ipv4 || ipv6) !== 'undefined'
    const isIP = !data.host && containsIP

    if (!('connections' in indexes)) {
      const idx = target.push({
        name: 'connections',
        type: 'indicator-type',
        items: [],
      })
      indexes.connections = idx - 1
    }

    const payload = {
      value: host,
      isIP,
      queryable: true,
    }

    storeValue(target[indexes.connections].items, payload, `network/connections/${payload.value}`)
  }

  const host = (
    hostIndicators,
    data,
    subtype,
  ) => {
    const { operation } = data

    const payloadByType = {
      file: ({ path }) => ({
        value: path,
        operation,
        hasParent: true,
      }),
      mutex: ({ name }) => ({
        value: name,
      }),
      'windows-registry': ({ value, key }) => ({
        key,
        value,
        operation,
        hasParent: true,
      }),
      directory: ({ name }) => ({
        value: name,
      }),
      process: ({ 'command-line': commandLine, name }) => ({
        value: commandLine === 'N/A' ? name : commandLine || name,
      }),
    }

    /**
     * Note:
     * This is a bug on the data side
     */
    if (subtype.match('file|windows-registry') && !operation) {
      return
    }

    if (!payloadByType[subtype]) {
      return
    }

    const payload = payloadByType[subtype](data)

    const keyParts = ['host', subtype, operation, payload.key, payload.value].filter(
      (i) => i !== undefined && i !== null
    )

    const key = keyParts.join('/')

    if (!(subtype in indexes)) {
      const idx = hostIndicators.push({
        name: subtype,
        type: 'indicator-type',
        items: [],
      })
      indexes[subtype] = idx - 1
    }

    if (operation && !(operation in indexes)) {
      const idx = hostIndicators[indexes[subtype]].items.push({
        name: operation,
        type: 'operation',
        items: [],
      })
      indexes[operation] = idx - 1
    }
    const subtypeIndex = indexes[subtype]
    const operationIndex = operation ? indexes[operation] : -1

    storeValue(
      operation
        ? hostIndicators[subtypeIndex].items[operationIndex].items
        : hostIndicators[subtypeIndex].items,
      payload,
      key
    )
  }
  return {
    detection,
    network,
    host,
  }
}

const getCounts = (indicators) => {
  return indicators.reduce((acc, { name, items = [] }) => {
    if (name.match('file|windows-registry')) {
      items.forEach(({ items = [] }) => {
        acc[name] = name in acc ? acc[name] + items.length : items.length
      })
    } else {
      acc[name] = items.length
    }
    return acc
  }, {})
}

export const normalizeFileReport = (rawResponse) => {
  if (!rawResponse) return null

  const {
    md5 = '',
    sha1 = '',
    sha256 = '',
    mimeType = 'Not Known',
    size,
    indicators: rawIndicators = [],
    extractedFiles: rawExtractedFiles = [],
  } = rawResponse

  const isAnalyzing = rawResponse?.status === 'SUBMITTED' || rawResponse?.status === 'IN_PROGRESS'

  if (isAnalyzing) {
    return {
      status: 'ANALYZING',
      md5,
      sha1,
      sha256,
      mimeType,
      size,
    }
  }

  const indicatorProcessors = processors()

  const { detection, network, host } = (rawIndicators || []).reduce(
    (acc, indicator) => {
      const type = indicator.type
      const data = indicator.data
      const subtype = indicator.subtype
      if (type in indicatorProcessors) {
        indicatorProcessors[type](acc[type], data, subtype)
      }
      return acc
    },
    { detection: [], network: [], host: [] }
  )

  const indicators = [...network, ...host]
  const indicatorsCount = getCounts(indicators)

  const extractedFiles = rawExtractedFiles || []
  const fileType = extractedFiles.length > 0 ? 'archive' : 'file'

  const uncompressedSize = extractedFiles.reduce((acc, file) => acc + file.size, 0)

  const mimeTypesCount = extractedFiles.reduce((acc, file) => {
    if (!acc[file.contentType]) {
      acc[file.contentType] = 0
    }
    acc[file.contentType] = acc[file.contentType] + 1
    return acc
  }, {})

  return {
    md5,
    sha1,
    sha256,
    mimeType,
    size,
    uncompressedSize,
    detection,
    indicators,
    indicatorsCount,
    fileType,
    extractedFiles,
    mimeTypesCount,
  }
}
